// src/features/tasks/components/TasksResultsSummary.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  AllInclusive,
  Event,
  Loop,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const VIEW_OPTIONS = [
  { value: 'cards', icon: ViewModuleIcon, labelKey: 'tasks:list_page.card_view' },
  { value: 'list', icon: ViewListIcon, labelKey: 'tasks:list_page.list_view' },
];

const RECURRANCE_OPTIONS = [
  { value: 'all', icon: AllInclusive, labelKey: 'tasks:list_page.all' },
  { value: 'adhock', icon: Event, labelKey: 'tasks:list_page.adhock' },
  { value: 'recurring', icon: Loop, labelKey: 'tasks:list_page.recurring' },
];

const FILTER_OPTIONS = [
  { value: 'all', labelKey: 'tasks:filtering.all' },
  { value: 'my-tasks', labelKey: 'tasks:filtering.my_tasks' },
  { value: 'today', labelKey: 'tasks:filtering.today' },
  { value: 'overdue', labelKey: 'tasks:filtering.overdue_tasks' },
  { value: 'pending', labelKey: 'tasks:filtering.pending' },
  { value: 'in-progress', labelKey: 'tasks:filtering.in_progress' },
  { value: 'completed', labelKey: 'tasks:filtering.completed' },
];

// LocalStorage key for view mode preference
const VIEW_MODE_STORAGE_KEY = 'tasks_view_mode';

// Utility functions for localStorage operations
const saveViewModeToStorage = (viewMode) => {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  } catch (error) {
    console.warn('Failed to save view mode to localStorage:', error);
  }
};

const getViewModeFromStorage = () => {
  try {
    return localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to get view mode from localStorage:', error);
    return null;
  }
};

export default function TasksResultsSummary({
  filteredTasksCount,
  totalTasksCount,
  currentFilter,
  selectedCategory,
  searchTerm,
  categories,
  viewMode,
  onViewModeChange,
  onFilterChange,
  recurrenceFilter,
  onRecurrenceFilterChange,
}) {
  const { t } = useTranslation(['tasks']);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const currentFilterOption = FILTER_OPTIONS.find(opt => opt.value === currentFilter);
  const currentFilterLabel = currentFilterOption ? t(currentFilterOption.labelKey) : currentFilter;
  const selectedCategoryName = categories.find(c => c.category_id === selectedCategory)?.name;

  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Load saved view mode on component mount with responsive default
  useEffect(() => {
    const savedViewMode = getViewModeFromStorage();
    if (savedViewMode && savedViewMode !== viewMode) {
      // Only update if the saved view mode is different from current
      // and is a valid option
      const isValidViewMode = VIEW_OPTIONS.some(option => option.value === savedViewMode);
      if (isValidViewMode) {
        onViewModeChange(savedViewMode);
      }
    } else if (!savedViewMode && !viewMode) {
      // Set default view mode based on screen size if no saved preference and no current mode
      const defaultViewMode = isSmallScreen ? 'cards' : 'list';
      saveViewModeToStorage(defaultViewMode);
      onViewModeChange(defaultViewMode);
    }
  }, [isSmallScreen, viewMode, onViewModeChange]);

  // Handle view mode change with localStorage save
  const handleViewModeChange = (event, newViewMode) => {
    // Prevent deselecting all options (ToggleButtonGroup allows null)
    if (newViewMode !== null) {
      saveViewModeToStorage(newViewMode);
      onViewModeChange(newViewMode);
    }
  };

  // Handle recurrence filter change
  const handleRecurrenceFilterChange = (event, newRecurrence) => {
    // Prevent deselecting all options
    if (newRecurrence !== null) {
      onRecurrenceFilterChange(newRecurrence);
    }
  };

  const handleFilterSelect = (filter) => {
    onFilterChange(filter);
    setFilterMenuAnchor(null);
  };


  return (
    <Box display="flex" alignItems="stretch" mb={2} flexWrap="wrap" gap={3}>
      <Box>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
          fullWidth
          sx={{ height: '100%' }}
          size="small"
        >
          {currentFilterLabel}
        </Button>
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={() => setFilterMenuAnchor(null)}
        >
          {FILTER_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleFilterSelect(option.value)}
              selected={currentFilter === option.value}
            >
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      
      <Box flex={1}></Box>

      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        size="small"
        aria-label="view mode"
      >
        {VIEW_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <Tooltip key={option.value} title={t(option.labelKey)}>
              <ToggleButton
                value={option.value}
                aria-label={t(option.labelKey)}
              >
                <Icon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>

      <ToggleButtonGroup
        value={recurrenceFilter}
        exclusive
        onChange={handleRecurrenceFilterChange}
        size="small"
        aria-label="recurrence filter"
      >
        {RECURRANCE_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <Tooltip title={t(option.labelKey)}>
              <ToggleButton
                key={option.value}
                value={option.value}
                aria-label={t(option.labelKey)}
              >
                <Icon fontSize="small" sx={{ mr: 0.5 }} />
                {/* {t(option.labelKey)} */}
              </ToggleButton>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
}